import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { accountingApi, salesApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-ACC-03.01/02/03/08/09 — Customer invoices.
 * List/detail/create/send/pay/credit/write-off in one page.
 */
export function InvoicesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showCredit, setShowCredit] = useState(false);

  const listQuery = useQuery({
    queryKey: ['ar', 'invoices', statusFilter],
    queryFn: () => accountingApi.listInvoices({ status: statusFilter || undefined, limit: 200 }),
  });
  const deleteInvMut = useMutation({
    mutationFn: (id: string) => accountingApi.deleteInvoice(id),
    onSuccess: (r: any) => { toast.success(r?.message || 'Draft invoice deleted'); qc.invalidateQueries({ queryKey: ['ar', 'invoices'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = listQuery.data ?? [];
    let open = 0, overdue = 0, totalOutstanding = 0, paidThisView = 0;
    for (const inv of rows) {
      if (['open', 'partially_paid'].includes(inv.status)) open++;
      if (inv.status === 'overdue') overdue++;
      if (['open', 'partially_paid', 'overdue'].includes(inv.status)) {
        totalOutstanding += parseFloat(inv.balance_due) || 0;
      }
      if (inv.status === 'paid') paidThisView++;
    }
    return { total: rows.length, open, overdue, totalOutstanding, paidThisView };
  })();

  const STATUS_OPTIONS = [
    { value: '',               label: 'All' },
    { value: 'draft',          label: 'Draft' },
    { value: 'open',           label: 'Open' },
    { value: 'partially_paid', label: 'Partial' },
    { value: 'paid',           label: 'Paid' },
    { value: 'overdue',        label: 'Overdue' },
    { value: 'void',           label: 'Void' },
    { value: 'written_off',    label: 'Written off' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Receivables"
        title="Customer invoices"
        subtitle="Create, send, and collect on customer invoices. Record payments, issue credit notes, write off bad debt."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New invoice
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Open / partial" value={kpis.open}
          accent={kpis.open > 0 ? 'finance' : 'default'} />
        <KpiTile label="Overdue" value={kpis.overdue}
          accent={kpis.overdue > 0 ? 'danger' : 'default'} />
        <KpiTile label="Outstanding" value={`${fmtCurrency(kpis.totalOutstanding)}`} accent="finance" />
      </div>

      {/* Status chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto border-slate-200">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
        <div className="card overflow-hidden border-slate-200">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
           !listQuery.data?.length ? (
            <EmptyState
              message={statusFilter ? `No ${statusFilter.replace('_', ' ')} invoices` : 'No invoices yet'}
              hint={statusFilter ? 'Try a different filter.' : 'Issue your first customer invoice.'}
              action={!statusFilter && (
                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                  + New invoice
                </button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Invoice #</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Customer</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Due</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Balance</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(listQuery.data ?? []).map((inv: any, i: number) => (
                    <tr key={inv.id} onClick={() => setSelected(inv.id)}
                        className={`cursor-pointer transition-colors ${
                          selected === inv.id
                            ? 'bg-finance-50'
                            : i % 2 ? 'bg-slate-50/40 hover:bg-slate-50' : 'bg-white hover:bg-slate-50'
                        }`}>
                      <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{inv.invoice_number}</td>
                      <td className="px-4 py-2 text-sm text-slate-900">{inv.customer_name}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-600 tabular-nums">
                        {new Date(inv.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-2"><StatusBadge status={inv.status} /></td>
                      <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                        ${parseFloat(inv.balance_due).toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-right" onClick={(ev) => ev.stopPropagation()}>
                        {inv.status === 'draft' && (
                          <button
                            className="text-xs font-medium px-2 py-1 rounded transition-colors text-red-600 hover:bg-red-50"
                            disabled={deleteInvMut.isPending}
                            onClick={() => { if (window.confirm(`Delete draft invoice ${inv.invoice_number}?`)) deleteInvMut.mutate(inv.id); }}>
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px] border-slate-200">
          {!selected ? (
            <EmptyState
              message="Select an invoice"
              hint="Click a row on the left to view lines, record payments, or issue a credit note."
            />
          ) : (
            <InvoiceDetail
              invoiceId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['ar'] })}
              onPay={() => setShowPayment(true)}
              onCredit={() => setShowCredit(true)}
            />
          )}
        </div>
      </div>

      {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['ar', 'invoices'] });
          setShowCreate(false); setSelected(id);
        }} />}

      {showPayment && selected && <PaymentModal invoiceId={selected}
        onClose={() => setShowPayment(false)}
        onRecorded={() => {
          qc.invalidateQueries({ queryKey: ['ar'] });
          setShowPayment(false);
        }} />}

      {showCredit && selected && <CreditNoteModal invoiceId={selected}
        onClose={() => setShowCredit(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['ar'] });
          setShowCredit(false);
        }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    draft:           'bg-slate-100 text-slate-700 ring-slate-200',
    open:            'bg-finance-50 text-finance-800 ring-finance-200',
    partially_paid:  'bg-amber-50 text-amber-800 ring-amber-200',
    paid:            'bg-sage-50 text-sage-700 ring-sage-200',
    overdue:         'bg-red-50 text-red-700 ring-red-200',
    void:            'bg-slate-100 text-slate-500 ring-slate-200',
    written_off:     'bg-red-50 text-red-600 ring-red-200',
  }[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function InvoiceDetail({ invoiceId, onChanged, onPay, onCredit }:
    { invoiceId: string; onChanged: () => void; onPay: () => void; onCredit: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['ar', 'invoice', invoiceId], queryFn: () => accountingApi.getInvoice(invoiceId) });
  const [showWriteOff, setShowWriteOff] = useState(false);

  const sendMut = useMutation({
    mutationFn: (email?: string) => accountingApi.emailInvoice(invoiceId, email ? { recipientEmail: email } : undefined),
    onSuccess: (d) => { toast.success(`Sent to ${d.recipient}`); qc.invalidateQueries({ queryKey: ['ar', 'invoice', invoiceId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const writeOffMut = useMutation({
    mutationFn: (reason: string) => accountingApi.writeOffInvoice(invoiceId, reason),
    onSuccess: () => {
      toast.success('Written off');
      qc.invalidateQueries({ queryKey: ['ar'] });
      onChanged();
      setShowWriteOff(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const inv = q.data;
  const balance = parseFloat(inv.balance_due);
  const outstanding = balance > 0 && !['void','written_off'].includes(inv.status);

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-slate-500">{inv.invoice_number}</div>
          <div className="font-semibold">{inv.customer_name}</div>
          {inv.customer_email && <div className="text-xs text-slate-600">{inv.customer_email}</div>}
          <div className="text-xs text-slate-500">Due {new Date(inv.due_date).toLocaleDateString()}</div>
        </div>
        <StatusBadge status={inv.status} />
      </div>

      <table className="table mb-4">
        <thead><tr><th>#</th><th>Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
        <tbody>
          {inv.lines.map((l: any) => (
            <tr key={l.id}>
              <td className="text-xs">{l.line_no}</td>
              <td>
                <div className="text-xs">{l.description}</div>
                {l.product_name && <div className="font-mono text-2xs text-slate-500">{l.sku}</div>}
              </td>
              <td className="text-right">{parseFloat(l.quantity).toFixed(0)}</td>
              <td className="text-right">{fmtCurrency(parseFloat(l.unit_price))}</td>
              <td className="text-right text-xs">{fmtCurrency(parseFloat(l.tax_amount))}</td>
              <td className="text-right font-semibold">{fmtCurrency(parseFloat(l.line_total))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={5} className="text-right text-xs text-slate-500">Subtotal</td><td className="text-right">{fmtCurrency(parseFloat(inv.subtotal))}</td></tr>
          <tr><td colSpan={5} className="text-right text-xs text-slate-500">Tax</td><td className="text-right">{fmtCurrency(parseFloat(inv.tax_total))}</td></tr>
          <tr className="font-bold"><td colSpan={5} className="text-right">Grand total</td><td className="text-right">{fmtCurrency(parseFloat(inv.grand_total))}</td></tr>
          <tr><td colSpan={5} className="text-right text-xs">Paid</td><td className="text-right">{fmtCurrency(parseFloat(inv.paid_total))}</td></tr>
          <tr className="font-bold"><td colSpan={5} className="text-right">Balance due</td><td className="text-right text-lg">{fmtCurrency(balance)}</td></tr>
        </tfoot>
      </table>

      {inv.payments?.length > 0 && (
        <div className="mb-3">
          <h3 className="text-xs font-semibold text-slate-600 mb-1">Payment history</h3>
          <table className="table text-xs">
            <thead><tr><th>Payment #</th><th>Date</th><th>Method</th><th className="text-right">Applied</th></tr></thead>
            <tbody>
              {inv.payments.map((p: any, i: number) => (
                <tr key={i}>
                  <td className="font-mono">{p.payment_number}</td>
                  <td>{new Date(p.payment_date).toLocaleDateString()}</td>
                  <td>{p.method}</td>
                  <td className="text-right">{fmtCurrency(parseFloat(p.applied_amount))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {inv.dunning?.length > 0 && (
        <div className="mb-3 text-xs">
          <h3 className="text-xs font-semibold text-slate-600 mb-1">Dunning history</h3>
          {inv.dunning.map((d: any, i: number) => (
            <div key={i} className="text-xs text-slate-600">
              Level {d.notice_level} notice sent {new Date(d.sent_at).toLocaleDateString()} ({d.days_overdue} days overdue)
            </div>
          ))}
        </div>
      )}

      {inv.written_off_at && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded mb-3">
          Written off {new Date(inv.written_off_at).toLocaleString()} by {inv.written_off_by_name}: {inv.write_off_reason}
          <div className="mt-1">JE: {inv.write_off_je_number}</div>
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 flex gap-2 justify-end flex-wrap">
        {outstanding && (
          <>
            <button className="btn-secondary" disabled={sendMut.isPending} onClick={() => sendMut.mutate(undefined)}>
              {inv.sent_at ? 'Resend' : 'Send email'}
            </button>
            <button className="btn-secondary" onClick={onCredit}>Issue credit</button>
            <button className="btn-secondary" disabled={writeOffMut.isPending}
              onClick={() => setShowWriteOff(true)}>
              Write off
            </button>
            <button className="btn-primary" onClick={onPay}>Record payment</button>
          </>
        )}
      </div>

      {showWriteOff && (
        <WriteOffDialog
          invoiceNumber={inv.invoice_number}
          balance={balance}
          loading={writeOffMut.isPending}
          onClose={() => setShowWriteOff(false)}
          onConfirm={(reason) => writeOffMut.mutate(reason)}
        />
      )}
    </div>
  );
}

function WriteOffDialog({ invoiceNumber, balance, loading, onClose, onConfirm }: {
  invoiceNumber: string; balance: number; loading: boolean; onClose: () => void; onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Accounting · Write-off</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Write off invoice?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Write off <span className="font-mono font-semibold text-ink-900">{invoiceNumber}</span>
          {' '}— balance <span className="font-semibold text-ink-900">{fmtCurrency(balance)}</span>?
          A journal entry will be posted to bad-debt expense. This is irreversible.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Write-off reason <span className="text-danger">*</span></label>
        <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Customer liquidated, debt deemed uncollectible" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700" disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}>
          {loading ? 'Writing off…' : 'Write off invoice'}
        </button>
      </div>
    </Modal>
  );
}

function CreateInvoiceModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })();

  const [customerId, setCustomerId] = useState('');
  const [dueDate, setDueDate] = useState(in30);
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Array<any>>([
    { description: '', quantity: 1, unitPrice: 0, discountAmount: 0, taxRate: 0 },
  ]);

  const custQuery = useQuery({ queryKey: ['customers'], queryFn: () => salesApi.listCustomers({ limit: 200 }) });

  const mut = useMutation({
    mutationFn: () => accountingApi.createInvoice({
      customerId, dueDate, paymentTerms, notes: notes || undefined,
      autoPost: true,
      lines: lines.filter((l) => l.description.trim() && l.unitPrice > 0),
    }),
    onSuccess: (d) => { toast.success(`Invoice ${d.invoiceNumber} created. JE: ${d.journalEntryNumber ?? 'n/a'}`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const subtotal = lines.reduce((s, l) => s + (l.quantity * l.unitPrice - (l.discountAmount || 0)), 0);
  const taxTotal = lines.reduce((s, l) => s + (l.quantity * l.unitPrice - (l.discountAmount || 0)) * (l.taxRate / 100), 0);
  const grandTotal = subtotal + taxTotal;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Accounting · Receivables</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New invoice</h2>
      </div>

      <div className="px-6 py-4 grid grid-cols-3 gap-3 flex-shrink-0">
        <div>
          <label className="label">Customer</label>
          <select className="input" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Select…</option>
            {custQuery.data?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Due date</label>
          <input type="date" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Terms</label>
          <input className="input" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="border border-slate-200 rounded-xl overflow-hidden mb-3">
          <table className="table">
            <thead><tr><th>Description</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Disc</th><th className="text-right">Tax %</th><th className="w-8"></th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td><input className="input text-xs" value={l.description}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /></td>
                  <td className="text-right"><input type="number" min={0.01} step={1} className="input w-20 text-right tabular-nums" value={l.quantity}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} step={0.01} className="input w-24 text-right tabular-nums" value={l.unitPrice}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitPrice: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} step={0.01} className="input w-20 text-right tabular-nums" value={l.discountAmount}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, discountAmount: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} max={100} step={0.1} className="input w-16 text-right tabular-nums" value={l.taxRate}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, taxRate: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td>
                    <button className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                      onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="text-sm font-medium text-ink-700 hover:text-ink-900 transition-colors mb-3"
          onClick={() => setLines((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0, discountAmount: 0, taxRate: 0 }])}>
          + Add line
        </button>
        <div className="text-right mb-3">
          <div className="text-xs text-slate-500">Subtotal ${subtotal.toFixed(2)} + Tax ${taxTotal.toFixed(2)}</div>
          <div className="font-display font-bold text-xl text-ink-900 tabular-nums">Total: ${grandTotal.toFixed(2)}</div>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-slate-200 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!customerId || grandTotal <= 0 || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create & post'}
        </button>
      </div>
    </Modal>
  );
}

function PaymentModal({ invoiceId, onClose, onRecorded }:
    { invoiceId: string; onClose: () => void; onRecorded: () => void }) {
  const q = useQuery({ queryKey: ['ar', 'invoice', invoiceId], queryFn: () => accountingApi.getInvoice(invoiceId) });
  const balance = q.data ? parseFloat(q.data.balance_due) : 0;
  const [amount, setAmount] = useState(balance);
  const [method, setMethod] = useState<'cash'|'check'|'wire'|'ach'|'card'|'other'>('wire');
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));

  const mut = useMutation({
    mutationFn: () => accountingApi.recordCustomerPayment({
      customerId: q.data.customer_id, paymentDate, method, amount, reference: reference || undefined,
      applications: [{ invoiceId, amount }],
    }),
    onSuccess: (d) => { toast.success(`Payment ${d.paymentNumber} recorded`); onRecorded(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading || !q.data) return null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Accounting · Payment</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Record payment</h2>
        <div className="mt-2 bg-slate-50 rounded-xl px-4 py-3 text-sm">
          <span className="font-mono font-semibold text-slate-900">{q.data.invoice_number}</span>
          <span className="text-slate-500 mx-2">·</span>
          Balance due <span className="font-display font-bold text-slate-900">{fmtCurrency(balance)}</span>
        </div>
      </div>
      <div className="px-6 py-5 grid grid-cols-2 gap-3">
        <div>
          <label className="label">Amount</label>
          <input type="number" min={0.01} max={balance} step={0.01} className="input tabular-nums" value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Payment date</label>
          <input type="date" className="input" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Method</label>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value as any)}>
            <option value="cash">Cash</option><option value="check">Check</option>
            <option value="wire">Wire</option><option value="ach">ACH</option>
            <option value="card">Card</option><option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="label">Reference <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="check#, wire ref" />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={amount <= 0 || amount > balance + 0.01 || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Recording…' : `Record ${fmtCurrency(amount)}`}
        </button>
      </div>
    </Modal>
  );
}

function CreditNoteModal({ invoiceId, onClose, onCreated }:
    { invoiceId: string; onClose: () => void; onCreated: () => void }) {
  const q = useQuery({ queryKey: ['ar', 'invoice', invoiceId], queryFn: () => accountingApi.getInvoice(invoiceId) });
  const balance = q.data ? parseFloat(q.data.balance_due) : 0;
  const [amount, setAmount] = useState(balance);
  const [reason, setReason] = useState('');
  const [applyToThis, setApplyToThis] = useState(true);

  const mut = useMutation({
    mutationFn: () => accountingApi.createCreditNote({
      customerId: q.data.customer_id, sourceInvoiceId: invoiceId,
      amount, reason, applyToInvoiceId: applyToThis ? invoiceId : undefined,
    }),
    onSuccess: (d) => {
      toast.success(`Credit note ${d.creditNumber} ${applyToThis ? 'issued & applied' : 'issued'}`);
      onCreated();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading || !q.data) return null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Accounting · Credit note</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Issue credit note</h2>
        <div className="mt-2 bg-slate-50 rounded-xl px-4 py-3 text-sm">
          <span className="font-mono font-semibold text-slate-900">{q.data.invoice_number}</span>
          <span className="text-slate-500 mx-2">·</span>
          Balance <span className="font-semibold text-slate-900">{fmtCurrency(balance)}</span>
        </div>
      </div>
      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Credit amount</label>
          <input type="number" min={0.01} step={0.01} className="input tabular-nums" value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <label className="label">Reason <span className="text-danger">*</span></label>
          <textarea className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <label className="flex items-center gap-2.5 text-sm text-ink-700 cursor-pointer">
          <input type="checkbox" checked={applyToThis} onChange={(e) => setApplyToThis(e.target.checked)} />
          Apply immediately to this invoice
        </label>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!reason.trim() || amount <= 0 || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create credit'}
        </button>
      </div>
    </Modal>
  );
}
